import { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { captureLead, formatError } from "@/lib/api";
import { track } from "@/lib/analytics";

/**
 * Reusable premium email capture. No popups — inline, elegant.
 * variant: 'dark' (on black) | 'light' (on white)
 */
export function NewsletterForm({
  sourcePage,
  tags = ["Newsletter"],
  placeholder = "Enter your email",
  buttonText = "Subscribe",
  variant = "dark",
  testId = "newsletter-form",
  onDone,
}) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const dark = variant === "dark";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email.");
      return;
    }
    setState("loading");
    setError("");
    try {
      await captureLead({ email: email.trim(), source_page: sourcePage, tags });
      track.newsletter(sourcePage, tags);
      setState("done");
      onDone && onDone();
    } catch (err) {
      setState("error");
      setError(formatError(err.response?.data?.detail) || "Please try again.");
    }
  };

  if (state === "done") {
    return (
      <div
        data-testid={`${testId}-success`}
        className={`flex items-center gap-3 ${dark ? "text-white" : "text-black"}`}
      >
        <span className="w-9 h-9 border border-[#D4AF37] flex items-center justify-center shrink-0">
          <Check size={16} className="text-[#D4AF37]" />
        </span>
        <span className="font-body">You’re in. Watch your inbox.</span>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={testId} className="w-full max-w-md">
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testId}-email`}
          className={`flex-1 px-5 py-4 font-body outline-none transition-colors ${
            dark
              ? "bg-black border border-white/25 text-white placeholder:text-white/40 focus:border-[#D4AF37]"
              : "bg-white border border-black/20 text-black placeholder:text-black/40 focus:border-[#D4AF37]"
          }`}
        />
        <button
          type="submit"
          disabled={state === "loading"}
          data-testid={`${testId}-submit`}
          className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E6C75A] text-black font-body font-medium px-7 py-4 transition-colors duration-300 disabled:opacity-60"
        >
          {state === "loading" ? "…" : buttonText}
          <ArrowRight size={16} />
        </button>
      </div>
      {error && <p className="font-body text-sm text-red-400 mt-3">{error}</p>}
    </form>
  );
}

export default NewsletterForm;
